import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Globe, Users, Calendar, Briefcase, ShoppingBag, MessageCircle, Shield, Heart, MapPin, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';

const Marketing = () => {
  const navigate = useNavigate();
  const isIOSApp = useIsIOSApp();

  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Interest Groups",
      description: "Join communities of like-minded expats who share your passions - from hiking to book clubs, food to fitness."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Local Events",
      description: "Discover meetups, gatherings, and events happening near you. Never miss an opportunity to connect."
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Job Board",
      description: "Find international opportunities with visa sponsorship and remote-friendly positions worldwide."
    },
    {
      icon: <ShoppingBag className="w-6 h-6" />,
      title: "Marketplace",
      description: "Buy and sell with fellow expats. Find deals on furniture, electronics, and more from trusted community members."
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "Direct Messaging",
      description: "Connect privately with other expats. Build friendships and professional relationships."
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Find Your People",
      description: "Search by nationality, location, or interests to find expats who understand your journey."
    }
  ];

  const testimonials = [
    {
      quote: "ExpatsSphere helped me find my community in a new city within weeks. The events feature is amazing!",
      author: "Maria S.",
      location: "American in Budapest"
    },
    {
      quote: "Finally, a platform that understands what expats need. I found my job and my best friends here.",
      author: "Ahmed K.",
      location: "Egyptian in Dubai"
    },
    {
      quote: "The private groups feature lets us organize meetups for our nationality community easily.",
      author: "Chen L.",
      location: "Chinese in Singapore"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-full mb-8">
              <Globe className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
              Find Your People.<br />
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Anywhere.</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              ExpatsSphere connects expats worldwide. Discover events, join communities, 
              find jobs, and build lasting friendships in any city around the world.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-lg px-8"
                onClick={() => navigate('/register')}
              >
                Join FREE — 2 Full Years, No Card Needed
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8"
                onClick={() => navigate('/events')}
              >
                Explore Events
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              {isIOSApp ? 'Free to join • Full access • Connect with expats worldwide' : 'No credit card required • Full access • Cancel anytime'}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Everything You Need</h2>
            <p className="text-muted-foreground text-lg">One platform for all your expat community needs</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-background rounded-2xl p-6 border-2 border-border hover:border-primary/30 transition-colors"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary to-accent">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">28+</div>
              <div className="text-white/80">Active Members</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">24</div>
              <div className="text-white/80">Interest Groups</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">50+</div>
              <div className="text-white/80">Nationalities</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">15+</div>
              <div className="text-white/80">Cities Worldwide</div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Loved by Expats Worldwide</h2>
            <p className="text-muted-foreground text-lg">See what our community members are saying</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border-2 border-border"
              >
                <div className="flex gap-1 text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <p className="text-lg mb-4 italic">"{testimonial.quote}"</p>
                <div>
                  <div className="font-semibold">{testimonial.author}</div>
                  <div className="text-sm text-muted-foreground">{testimonial.location}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust Section */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
            <Shield className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-3xl font-heading font-bold mb-4">Safe & Trusted Community</h2>
          <p className="text-muted-foreground text-lg mb-8">
            Your privacy and safety are our top priorities. We verify users, moderate content, 
            and provide tools to keep your expat experience positive and secure.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Shield className="w-5 h-5 text-green-600" />
              <span>Verified Profiles</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Heart className="w-5 h-5 text-red-500" />
              <span>Community Moderated</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Globe className="w-5 h-5 text-primary" />
              <span>Global Support</span>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 to-accent/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Ready to Find Your People?
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            Join thousands of expats who have found their community on ExpatsSphere.
          </p>
          <Button 
            size="lg" 
            className="bg-primary hover:bg-primary/90 text-lg px-12"
            onClick={() => navigate('/register')}
          >
            Get Started FREE
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Marketing;
